import { state } from "../state";
import { formatBytes } from "../utils/file-utils";
import { mapSkeletonVersionToRuntime } from "../utils/spine-utils";
import i18next from "i18next";

export function updateSpineDuration(duration: number, time: number) {
  const infoContent = document.getElementById("info-content");
  if (!infoContent) return;
  
  const durationEl = infoContent.querySelector<HTMLElement>('[data-field="duration"]');
  const progressEl = infoContent.querySelector<HTMLElement>('[data-field="progress"]');
  
  if (durationEl) {
    durationEl.textContent = duration > 0 ? `${duration.toFixed(3)}s` : "-";
  }
  if (progressEl) {
    progressEl.textContent = `${time.toFixed(3)}s`;
  }
}

export function updateSpineInfoBox(entryId: string, metadata?: any) {
  const infoContent = document.getElementById("info-content");
  const animationList = document.getElementById("animation-list");
  const skinList = document.getElementById("skin-list");
  
  if (!infoContent) return;

  const entry = state.spineEntries.find((e) => e.id === entryId) ?? null;
  const meta = state.spineMetaById[entryId];

  let skeletonPath =
    meta?.skeletonPath ?? entry?.skelUrl ?? entry?.jsonUrl ?? undefined;
  let atlasPath = meta?.atlasPath ?? entry?.atlasUrl ?? undefined;
  let version = meta?.version ?? undefined;
  let runtimeVersion = meta?.runtimeVersion ?? undefined;
  let animationNames = meta?.animationNames ?? undefined;
  let skinNames = meta?.skinNames ?? undefined;
  let atlasPageCount = meta?.atlasPageCount ?? undefined;
  const totalBytes = meta?.totalBytes;

  if (metadata) {
      if (metadata.animations) animationNames = metadata.animations;
      if (metadata.skins) skinNames = metadata.skins;
      if (metadata.version) version = metadata.version;
  }

  if (!runtimeVersion && version) {
    runtimeVersion = mapSkeletonVersionToRuntime(version);
  }

  // Update meta cache
  if (meta) {
      if (animationNames) meta.animationNames = animationNames;
      if (skinNames) meta.skinNames = skinNames;
      if (version) meta.version = version;
      state.spineMetaById[entryId] = meta;
  }

  // Helper to create chips
  const createChip = (text: string, value: string, isActive: boolean) => {
      const chip = document.createElement("div");
      chip.className = "list-chip" + (isActive ? " active" : "");
      chip.textContent = text;
      chip.dataset.value = value;
      return chip;
  };

  // Update Animation List
  if (animationList) {
      const currentActive = animationList.querySelector('.list-chip.active') as HTMLElement;
      let currentVal = currentActive?.dataset.value;
      
      animationList.innerHTML = '';

      if (!animationNames || animationNames.length === 0) {
          const emptyChip = document.createElement("span");
          emptyChip.style.color = "#888";
          emptyChip.style.fontSize = "12px";
          emptyChip.dataset.i18n = "notDetected";
          emptyChip.textContent = i18next.t("notDetected");
          animationList.appendChild(emptyChip);
      } else {
          // If no active value or current value is not in the list, default to the first animation
          if (currentVal === undefined || currentVal === "" || !animationNames.includes(currentVal)) {
              currentVal = animationNames[0];
              // Send message to player to play the new default animation
              if (state.currentIframe) {
                  state.currentIframe.contentWindow?.postMessage({ 
                      type: 'set_animation', 
                      payload: { name: currentVal } 
                  }, '*');
                  // Ensure it plays when a valid animation is selected automatically
                  state.currentIframe.contentWindow?.postMessage({ type: 'play' }, '*');
              }
          }

          animationNames.forEach(name => {
              animationList.appendChild(createChip(name, name, currentVal === name));
          });
      }
  }

  // Update Skin List
  if (skinList) {
      const currentActive = skinList.querySelector('.list-chip.active') as HTMLElement;
      let currentVal = currentActive?.dataset.value;

      skinList.innerHTML = '';
      
      if (!skinNames || skinNames.length === 0) {
          const emptyChip = document.createElement("span");
          emptyChip.style.color = "#888";
          emptyChip.style.fontSize = "12px";
          emptyChip.dataset.i18n = "notDetected";
          emptyChip.textContent = i18next.t("notDetected");
          skinList.appendChild(emptyChip);
      } else {
          // If no active value or current value is not in the list, default to the first skin
          if (currentVal === undefined || currentVal === "" || !skinNames.includes(currentVal)) {
              currentVal = skinNames[0];
              // Send message to player
              if (state.currentIframe) {
                  state.currentIframe.contentWindow?.postMessage({ 
                      type: 'set_skin', 
                      payload: { name: currentVal } 
                  }, '*');
              }
          }

          skinNames.forEach(name => {
              skinList.appendChild(createChip(name, name, currentVal === name));
          });
      }
  }

  // Update Info Content
  const name = entry?.name ?? entryId;
  const sourceType = meta?.sourceType;
  const sizeText = formatBytes(totalBytes);

  infoContent.innerHTML = "";

  const createRow = (label: string, value: string) => {
      const div = document.createElement("div");
      div.className = "info-row";
      div.textContent = `${label}: ${value}`;
      return div;
  };

  const title = document.createElement("div");
  title.className = "info-title";
  title.dataset.i18n = "resourceInfo";
  title.textContent = i18next.t("resourceInfo");
  infoContent.appendChild(title);

  infoContent.appendChild(createRow(i18next.t("name"), name));
  if (skeletonPath) infoContent.appendChild(createRow(i18next.t("skeleton"), skeletonPath));
  if (atlasPath) infoContent.appendChild(createRow(i18next.t("atlas"), atlasPath));
  if (sourceType) infoContent.appendChild(createRow(i18next.t("source"), sourceType === "local" ? i18next.t("sourceLocal") : i18next.t("sourceManifest")));
  if (version) {
    infoContent.appendChild(createRow(i18next.t("spineVersion"), version));
  }

  if (runtimeVersion) {
    infoContent.appendChild(createRow(i18next.t("runtimeVersion"), runtimeVersion));
  }
  if (typeof atlasPageCount === "number") infoContent.appendChild(createRow(i18next.t("atlasPages"), String(atlasPageCount)));
  if (sizeText !== "-") infoContent.appendChild(createRow(i18next.t("totalSize"), sizeText));
  
  if (animationNames) infoContent.appendChild(createRow(i18next.t("animationCount"), String(animationNames.length)));
  if (skinNames) infoContent.appendChild(createRow(i18next.t("skinCount"), String(skinNames.length)));

  const titleState = document.createElement("div");
  titleState.className = "info-title";
  titleState.dataset.i18n = "playbackState";
  titleState.textContent = i18next.t("playbackState");
  titleState.style.marginTop = "10px";
  infoContent.appendChild(titleState);

  const durationLine = document.createElement("div");
  durationLine.className = "info-row";
  durationLine.innerHTML = `<span data-i18n="duration">${i18next.t("duration")}</span> <span data-field="duration">-</span>`;
  infoContent.appendChild(durationLine);

  const progressLine = document.createElement("div");
  progressLine.className = "info-row";
  progressLine.innerHTML = `<span data-i18n="time">${i18next.t("time")}</span> <span data-field="progress">-</span>`;
  infoContent.appendChild(progressLine);
}

export function updateSpineInfoBoxError(entryId: string, message: string) {
  const infoContent = document.getElementById("info-content");
  if (!infoContent) return;

  // Switch to info tab
  const tab = document.querySelector('.panel-tab[data-tab="info"]') as HTMLElement;
  if (tab) tab.click();

  infoContent.innerHTML = "";
  
  const title = document.createElement("div");
  title.className = "info-title";
  title.dataset.i18n = "errorInfo";
  title.textContent = i18next.t("errorInfo");
  title.style.color = "#ff5555";
  infoContent.appendChild(title);

  const msgDiv = document.createElement("div");
  msgDiv.className = "info-row";
  msgDiv.style.whiteSpace = "pre-wrap";
  msgDiv.style.color = "#ff8888";
  msgDiv.textContent = message;
  infoContent.appendChild(msgDiv);

  try {
    console.error("Spine load error for entry:", entryId, message);
  } catch {
  }
}
